import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MediaCard from "@/components/base/MediaCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { movieList } from "@/mocks/movies";
import { tvList } from "@/mocks/tvshows";
import type { Episode, MediaItem } from "@/types";

const movies = movieList as MediaItem[];
const tvshows = tvList as MediaItem[];
const allMedia: MediaItem[] = [...movies, ...tvshows];

export default function MediaDetail() {
  const { id } = useParams();
  const item = allMedia.find((m) => m.id === id);

  const [current, setCurrent] = useState<Episode | null>(
    item && item.episodes.length > 0 ? item.episodes[0] : null,
  );
  const [playing, setPlaying] = useState(false);
  const [copied, setCopied] = useState(false);

  usePageMeta({
    title: item
      ? item.type === "movie"
        ? `《${item.title}》-电影详情-四虎视频`
        : `《${item.title}》-电视剧详情-四虎视频`
      : "影视详情-四虎视频",
    description: item ? item.summary : "四虎视频影视详情页，展示影视作品的基本信息、简介与相关推荐。",
    keywords: item
      ? `${item.title},${item.genres.join(",")},${item.region},四虎视频`
      : "影视详情,四虎视频",
  });

  const related = useMemo(() => {
    if (!item) return [];
    const scored = allMedia
      .filter((m) => m.id !== item.id)
      .map((m) => {
        let score = 0;
        if (m.director === item.director) score += 3;
        score += m.genres.filter((g) => item.genres.includes(g)).length * 2;
        if (m.type === item.type) score += 1;
        return { m, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score || b.m.rating - a.m.rating);
    return scored.slice(0, 4).map((x) => x.m);
  }, [item]);

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-[900px] px-4 py-16 md:px-6">
        <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-900">
            影视信息暂缺
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            抱歉，未找到该影视作品，它可能已被移除或链接有误。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium whitespace-nowrap text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line text-base" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  const typeLabel = item.type === "movie" ? "电影" : "电视剧";

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", path: "/" },
          { label: typeLabel, path: item.type === "movie" ? "/category/movie" : "/category/tv" },
          { label: item.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
        <div className="mx-auto w-full max-w-[280px] lg:mx-0 lg:max-w-none">
          <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-200">
            <img
              src={item.cover}
              alt={`${item.title} 封面`}
              title={`${item.title} ${typeLabel}海报`}
              className="aspect-[3/4] h-auto w-full object-cover object-top"
            />
          </div>
        </div>

        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            <i className="ri-price-tag-3-line text-sm" />
            {typeLabel}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {item.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 rounded-lg bg-accent-500 px-3 py-2 text-background-50">
              <i className="ri-star-fill text-lg" />
              <span className="font-heading text-xl font-bold">{item.rating.toFixed(1)}</span>
              <span className="text-xs opacity-80">/ 10</span>
            </div>
            <span className="text-sm text-foreground-600">{item.status}</span>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-1.5 rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium whitespace-nowrap text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-share-forward-line text-base" />
              分享
            </button>
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
            <div className="flex gap-3">
              <dt className="w-14 shrink-0 text-foreground-500">类型</dt>
              <dd className="flex flex-wrap gap-1.5">
                {item.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/${item.type === "movie" ? "category/movie" : "category/tv"}?genre=${encodeURIComponent(g)}`}
                    className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {g}
                  </Link>
                ))}
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-14 shrink-0 text-foreground-500">地区</dt>
              <dd className="text-foreground-900">{item.region}</dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-14 shrink-0 text-foreground-500">年份</dt>
              <dd className="text-foreground-900">{item.year}</dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-14 shrink-0 text-foreground-500">时长</dt>
              <dd className="text-foreground-900">
                {item.type === "movie" ? `${item.duration} 分钟` : `单集约 ${item.duration} 分钟`}
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-14 shrink-0 text-foreground-500">导演</dt>
              <dd className="text-foreground-900">{item.director}</dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-14 shrink-0 text-foreground-500">主演</dt>
              <dd className="text-foreground-900">{item.actors.join(" / ")}</dd>
            </div>
          </dl>

          <div className="mt-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-950">作品简介</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.summary}</p>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="relative overflow-hidden rounded-lg bg-primary-950">
          <img
            src={item.cover}
            alt={`${item.title} 播放封面`}
            title={`${item.title} 演示播放器`}
            className="h-[240px] w-full object-cover object-top opacity-40 md:h-[420px]"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            {playing ? (
              <>
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-primary-950">
                  <i className="ri-pause-fill text-3xl" />
                </span>
                <p className="mt-4 font-heading text-lg font-semibold text-background-50 md:text-xl">
                  正在播放：{current ? `第 ${current.number} 集 · ${current.title}` : item.title}
                </p>
                <p className="mt-2 text-xs text-background-100/70 md:text-sm">
                  当前为前端演示播放器，暂未接入真实视频资源。
                </p>
                <button
                  type="button"
                  onClick={() => setPlaying(false)}
                  className="mt-4 rounded-md border border-background-50/40 px-4 py-2 text-sm font-medium whitespace-nowrap text-background-50 transition-colors hover:bg-background-50/10"
                >
                  停止播放
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  aria-label="播放"
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-primary-950 transition-transform hover:scale-105 md:h-20 md:w-20"
                >
                  <i className="ri-play-fill text-4xl" />
                </button>
                <p className="mt-4 font-heading text-lg font-semibold text-background-50 md:text-xl">
                  {item.type === "movie" ? `《${item.title}》正片` : `《${item.title}》第 1 集`}
                </p>
                <p className="mt-2 text-xs text-background-100/70 md:text-sm">
                  点击播放体验演示效果，播放器为前端占位，无真实视频资源。
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {item.type === "tv" && item.episodes.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-foreground-950">
            剧集目录
            <span className="ml-2 text-sm font-normal text-foreground-500">
              共 {item.episodes.length} 集
            </span>
          </h2>
          <div className="no-scrollbar mt-4 flex gap-3 overflow-x-auto pb-2">
            {item.episodes.map((ep) => {
              const active = current?.id === ep.id;
              return (
                <button
                  key={ep.id}
                  type="button"
                  onClick={() => {
                    setCurrent(ep);
                    setPlaying(true);
                  }}
                  className={`flex w-40 shrink-0 flex-col items-start rounded-lg border px-3 py-3 text-left transition-colors ${
                    active
                      ? "border-accent-400 bg-accent-50"
                      : "border-background-200/70 bg-background-50 hover:border-primary-200"
                  }`}
                >
                  <span
                    className={`text-xs font-medium ${active ? "text-accent-700" : "text-foreground-500"}`}
                  >
                    第 {ep.number} 集
                  </span>
                  <span className="mt-1 line-clamp-1 text-sm font-medium text-foreground-950">
                    {ep.title}
                  </span>
                  <span className="mt-1 text-xs text-foreground-500">{ep.duration} 分钟</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10 border-t border-background-200/70 pt-8">
        <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
        <p className="mt-2 text-sm text-foreground-600">根据类型与导演为你推荐以下相似作品</p>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {related.map((m) => (
            <MediaCard key={m.id} item={m} />
          ))}
        </div>
      </div>

      {copied && (
        <div className="fixed bottom-6 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-2 rounded-md bg-foreground-950 px-4 py-2.5 text-sm text-background-50 animate-fade-up">
          <i className="ri-checkbox-circle-line text-base text-accent-400" />
          链接已复制到剪贴板
        </div>
      )}
    </div>
  );
}